import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ChevronRight,
  Edit,
  Save,
  Target,
  Heart,
  Trophy,
  Check,
  X,
  MessageSquare
} from "lucide-react";
import NestedHauoraRings from "../components/NestedHauoraRings";
import MoodScale from "../components/MoodScale";
import LockedFeature from "../components/LockedFeature";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, isChallenger, updateUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [whakatauki, setWhakatauki] = useState("");
  const [coachNote, setCoachNote] = useState(null);
  const [coachNoteDismissed, setCoachNoteDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRingDetails, setShowRingDetails] = useState(false);
  const [editingGoals, setEditingGoals] = useState(false);
  const [yearGoal, setYearGoal] = useState("");
  const [myWhy, setMyWhy] = useState("");
  const [morningMood, setMorningMood] = useState(null);
  const [savingMood, setSavingMood] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, whakataukiRes, coachNoteRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: { user: {}, todayRings: {}, pouCompletedToday: 0, todayMoods: [] } })),
        axios.get(`${API}/whakatauki`).catch(() => ({ data: { text: "Kia kaha, kia māia, kia manawanui." } })),
        axios.get(`${API}/coach-notes/current`).catch(() => ({ data: null }))
      ]);
      
      // Set stats with defaults
      const statsData = statsRes.data || { user: {}, todayRings: {}, pouCompletedToday: 0, todayMoods: [] };
      setStats(statsData);
      setWhakatauki(whakataukiRes.data?.text || "Kia kaha, kia māia, kia manawanui.");
      setCoachNote(coachNoteRes.data);
      setYearGoal(statsData.user?.yearGoal || "");
      setMyWhy(statsData.user?.myWhy || "");
      
      const morning = statsData.todayMoods?.find(m => m.timeOfDay === "morning");
      if (morning) setMorningMood(morning.mood);
    } catch (err) {
      console.error("Dashboard error:", err);
      // Set empty defaults so page still renders
      setStats({ user: {}, todayRings: {}, pouCompletedToday: 0, todayMoods: [] });
      setWhakatauki("Kia kaha, kia māia, kia manawanui.");
    } finally {
      setLoading(false);
    }
  };

  const togglePouCompletion = async (pillar) => {
    if (!isChallenger()) return;
    
    const today = stats?.today || new Date().toISOString().split('T')[0];
    const currentCheckin = stats?.todayCheckin || {
      tinanaCompleted: false,
      hinengaroCompleted: false,
      wairuaCompleted: false,
      whanauCompleted: false
    };
    
    const fieldMap = {
      tinana: "tinanaCompleted",
      hinengaro: "hinengaroCompleted",
      wairua: "wairuaCompleted",
      whanau: "whanauCompleted"
    };
    
    const newValue = !currentCheckin[fieldMap[pillar]];
    
    try {
      await axios.post(`${API}/pou/checkin`, {
        date: today,
        tinanaCompleted: pillar === "tinana" ? newValue : currentCheckin.tinanaCompleted,
        hinengaroCompleted: pillar === "hinengaro" ? newValue : currentCheckin.hinengaroCompleted,
        wairuaCompleted: pillar === "wairua" ? newValue : currentCheckin.wairuaCompleted,
        whanauCompleted: pillar === "whanau" ? newValue : currentCheckin.whanauCompleted
      });
      fetchDashboardData();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const saveYearGoal = async () => {
    try {
      const res = await axios.put(`${API}/auth/year-goal`, { yearGoal, myWhy });
      updateUser(res.data);
      toast.success("Goals saved");
      setEditingGoals(false);
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const saveMood = async (mood) => {
    if (!isChallenger()) return;
    setSavingMood(true);
    try {
      await axios.post(`${API}/mood`, {
        date: stats?.today || new Date().toISOString().split('T')[0],
        timeOfDay: "morning",
        mood
      });
      setMorningMood(mood);
      toast.success("Mood logged");
    } catch (err) {
      toast.error("Failed to save mood");
    } finally {
      setSavingMood(false);
    }
  };

  const completeChallenge = async () => {
    if (!stats?.currentChallenge) return;
    try {
      await axios.post(`${API}/challenges/${stats.currentChallenge.id}/complete`, {
        shareToFeed: false
      });
      toast.success("Challenge completed! Ka pai!");
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete");
    }
  };

  // Taha (pillar) definitions with correct terminology
  const tahaList = [
    { key: "wairua", name: "Taha Wairua", shortName: "Wairua", color: "#5B8BA0", desc: "Spiritual" },
    { key: "hinengaro", name: "Taha Hinengaro", shortName: "Hinengaro", color: "#D4A84B", desc: "Mental" },
    { key: "tinana", name: "Taha Tinana", shortName: "Tinana", color: "#B85C4C", desc: "Physical" },
    { key: "whanau", name: "Taha Whānau", shortName: "Whānau", color: "#3D6B5A", desc: "Family" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }

  const todayRings = stats?.todayRings || {};
  const pouCompleted = stats?.pouCompletedToday || 0;
  const canAccessRings = isChallenger();

  return (
    <div className="p-5 pb-8 max-w-lg mx-auto" data-testid="dashboard">
      {/* COACH NOTE BANNER - Top of page */}
      {coachNote && !coachNoteDismissed && (
        <Card className="bg-gradient-to-r from-[#0B3D2E] to-[#0B3D2E]/90 border-0 rounded-2xl mb-6" data-testid="coach-note-banner">
          <CardContent className="p-5 relative">
            <button 
              onClick={() => setCoachNoteDismissed(true)}
              className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
              data-testid="dismiss-coach-note"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 pr-4">
                <p className="text-white/60 text-xs mb-1">From Coach {coachNote.coachName}</p>
                <p className="text-white font-brand text-lg mb-1">{coachNote.title}</p>
                <p className="text-white/80 text-sm leading-relaxed">{coachNote.message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header - TRIBE emphasis */}
      <div className="text-center mb-6">
        <p className="text-xs text-[#8FA39B] tracking-[0.3em] uppercase">The</p>
        <h1 className="text-4xl font-brand text-[#0B3D2E] tracking-wide my-1">TRIBE</h1>
        <p className="text-xs text-[#8FA39B] tracking-[0.3em] uppercase">App</p>
      </div>

      {/* HAUORA RINGS with Taha Legend */}
      <div className="relative mb-6">
        <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
          <CardContent className="p-6">
            <p className="text-xs text-[#8FA39B] uppercase tracking-wider mb-4 text-center">
              Today&apos;s Hauora
            </p>
            
            <div className="flex items-start gap-4">
              {/* Rings */}
              <div className="flex-1 flex justify-center">
                <NestedHauoraRings
                  wairuaCompleted={todayRings.wairua?.completed}
                  hinengaroCompleted={todayRings.hinengaro?.completed}
                  tinanaCompleted={todayRings.tinana?.completed}
                  whanauCompleted={todayRings.whanau?.completed}
                  size={160}
                  onClick={() => canAccessRings && setShowRingDetails(true)}
                />
              </div>
              
              {/* Taha Legend */}
              <div className="flex flex-col gap-2 pt-2">
                {tahaList.map((taha) => (
                  <div key={taha.key} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: taha.color }}
                    />
                    <span className="text-[10px] text-[#4A5D56] whitespace-nowrap">
                      {taha.shortName}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[#051F17] font-medium text-center mt-4">
              Taha completed: <span className="text-[#0B3D2E]">{pouCompleted} / 4</span>
            </p>
            
            {pouCompleted === 4 && (
              <p className="text-sm text-[#0B3D2E] mt-2 text-center font-medium">
                Full Hauora Day! Ka pai!
              </p>
            )}
          </CardContent>
        </Card>
        
        {!canAccessRings && (
          <LockedFeature title="Hauora Rings" tier="challenger" />
        )}
      </div>

      {/* YEAR GOAL */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#0B3D2E]" />
              <h3 className="font-brand text-[#051F17]">Year Goal</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => editingGoals ? saveYearGoal() : setEditingGoals(true)}
              className="text-[#0B3D2E] text-xs h-8"
            >
              {editingGoals ? <><Save className="w-3 h-3 mr-1" /> Save</> : <><Edit className="w-3 h-3 mr-1" /> Edit</>}
            </Button>
          </div>
          
          {editingGoals ? (
            <Input
              placeholder="What's your main goal this year?"
              value={yearGoal}
              onChange={(e) => setYearGoal(e.target.value)}
              className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
            />
          ) : yearGoal ? (
            <p className="text-[#051F17]">{yearGoal}</p>
          ) : (
            <p className="text-[#8FA39B] text-sm">Set your year goal...</p>
          )}
        </CardContent>
      </Card>

      {/* MY WHY */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4 text-[#B85C4C]" />
            <h3 className="font-brand text-[#051F17]">My Why</h3>
          </div>
          
          {editingGoals ? (
            <Textarea
              placeholder="Why am I doing this?"
              value={myWhy}
              onChange={(e) => setMyWhy(e.target.value)}
              className="min-h-[60px] bg-[#F9F9F7] border-0 rounded-xl resize-none"
            />
          ) : myWhy ? (
            <p className="text-[#051F17] text-sm">{myWhy}</p>
          ) : (
            <p className="text-[#8FA39B] text-sm">What motivates you?</p>
          )}
        </CardContent>
      </Card>

      {/* WHAKATAUKĪ */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4">
        <CardContent className="p-6">
          <p className="font-whakatauki text-xl text-[#051F17] text-center leading-relaxed">
            &ldquo;{whakatauki}&rdquo;
          </p>
        </CardContent>
      </Card>

      {/* MOOD CHECK */}
      <div className="relative mb-4">
        <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
          <CardContent className="p-5">
            <p className="text-xs text-[#8FA39B] uppercase tracking-wider mb-4 text-center">
              How do you feel today?
            </p>
            <MoodScale 
              value={morningMood} 
              onChange={saveMood}
              disabled={savingMood || !canAccessRings}
            />
          </CardContent>
        </Card>
        
        {!canAccessRings && (
          <LockedFeature title="Mood Check" tier="challenger" />
        )}
      </div>

      {/* CHALLENGE OF THE WEEK */}
      {stats?.currentChallenge && (
        <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4 text-[#D4A84B]" />
              <h3 className="font-brand text-[#051F17]">Challenge of the Week</h3>
            </div>
            
            <p className="font-medium text-[#051F17] mb-2">{stats.currentChallenge.title}</p>
            <p className="text-sm text-[#4A5D56] mb-4">{stats.currentChallenge.description}</p>
            
            {stats.currentChallenge.completed ? (
              <div className="flex items-center gap-2 text-[#0B3D2E]">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Completed!</span>
              </div>
            ) : (
              <Button 
                onClick={completeChallenge}
                className="w-full h-12 bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full font-brand"
              >
                Mark Complete
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Updated to My Taha */}
      <div className="grid grid-cols-2 gap-3 mt-6">
        <Button 
          variant="outline"
          onClick={() => navigate("/journal")}
          className="h-12 rounded-xl border-[#0B3D2E]/20 text-[#0B3D2E]"
        >
          Daily Journal
        </Button>
        <Button 
          variant="outline"
          onClick={() => navigate("/pou")}
          className="h-12 rounded-xl border-[#0B3D2E]/20 text-[#0B3D2E]"
        >
          My Taha
        </Button>
      </div>

      {/* Ring Details Dialog */}
      <Dialog open={showRingDetails} onOpenChange={setShowRingDetails}>
        <DialogContent className="max-w-sm mx-4 p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="font-brand text-[#0B3D2E]">Today&apos;s Taha</DialogTitle>
          </DialogHeader>
          
          <div className="px-6 pb-6 space-y-3">
            {tahaList.map((taha) => {
              const isCompleted = todayRings[taha.key]?.completed;
              const goalTitle = todayRings[taha.key]?.title;
              
              return (
                <button
                  key={taha.key}
                  onClick={() => togglePouCompletion(taha.key)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                    isCompleted 
                      ? "bg-[#E3EBE5]" 
                      : "bg-[#F9F9F7] hover:bg-[#E3EBE5]/50"
                  }`}
                  data-testid={`toggle-${taha.key}`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: isCompleted ? taha.color : `${taha.color}20` }}
                  >
                    {isCompleted && (
                      <Check className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-[#051F17]">{taha.name}</p>
                    <p className="text-xs text-[#4A5D56]">{goalTitle || taha.desc}</p>
                  </div>
                  <div 
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      isCompleted ? "border-[#0B3D2E] bg-[#0B3D2E]" : "border-[#0B3D2E]/30"
                    }`}
                  >
                    {isCompleted && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
          
          <div className="p-4 bg-[#F9F9F7] border-t border-[#0B3D2E]/5">
            <Button 
              onClick={() => { setShowRingDetails(false); navigate("/pou"); }}
              variant="ghost"
              className="w-full text-[#0B3D2E]"
            >
              Edit Daily Goals
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
